import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Wallet,
  Settings2,
  Info,
  AlertTriangle,
  TrendingUp,
  Download
} from 'lucide-react';
import billingService, {
  SpendSummary,
  SpendRow
} from '../../services/whatsapp/billingService';
import BillingRatesDialog from './billing/BillingRatesDialog';

const CATEGORY_LABEL: Record<string, string> = {
  marketing: 'Marketing',
  utility: 'Utility',
  authentication: 'Authentication',
  service: 'Service',
  referral_conversion: 'Referral',
  unknown: 'Uncategorised'
};

const today = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => `${new Date().toISOString().slice(0, 7)}-01`;

/** Sorted highest-spend first — the rows worth acting on come first. */
const toRows = (map: Record<string, SpendRow>): SpendRow[] =>
  Object.values(map || {}).sort((a, b) => b.cost - a.cost);

/**
 * Billing — what WhatsApp messaging is costing.
 *
 * Meta bills per 24-hour conversation, not per message, so every figure here
 * counts conversations reported by Meta's webhooks and multiplies them by a
 * locally configured rate card. That makes it an estimate: close to the
 * invoice, but never reconciled to it. The page says so rather than implying
 * these are Meta's own numbers.
 */
const WhatsAppBilling: React.FC = () => {
  const [summary, setSummary] = useState<SpendSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ratesOpen, setRatesOpen] = useState(false);

  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await billingService.summary(from, to));
    } catch (err: any) {
      setError(err.message || 'Could not load billing data');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const money = useMemo(() => {
    const currency = summary?.currency || 'INR';
    return (value: number) => {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }).format(value);
      } catch {
        // An unrecognised currency code must not blank the page.
        return `${currency} ${value.toFixed(2)}`;
      }
    };
  }, [summary?.currency]);

  const exportCsv = () => {
    if (!summary) return;

    const rows: string[][] = [['Section', 'Name', 'Conversations', 'Cost']];
    toRows(summary.byCategory).forEach((row) =>
      rows.push([
        'Category',
        CATEGORY_LABEL[row.name] || row.name,
        String(row.conversations),
        row.cost.toFixed(2)
      ])
    );
    toRows(summary.byTemplate).forEach((row) =>
      rows.push(['Template', row.name, String(row.conversations), row.cost.toFixed(2)])
    );
    toRows(summary.byBroadcast).forEach((row) =>
      rows.push(['Campaign', row.name, String(row.conversations), row.cost.toFixed(2)])
    );

    const csv = rows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp-spend-${from}-to-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const budget = summary?.budget;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-3">
        <h1 className="mr-auto flex items-center gap-2 text-base font-semibold text-gray-900">
          <Wallet className="h-5 w-5 text-primary-600" />
          Billing
        </h1>

        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          From
          <input
            type="date"
            value={from}
            max={to}
            onChange={(event) => setFrom(event.target.value)}
            className="rounded-lg border-gray-300 py-1 text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
        </label>

        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          To
          <input
            type="date"
            value={to}
            min={from}
            max={today()}
            onChange={(event) => setTo(event.target.value)}
            className="rounded-lg border-gray-300 py-1 text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
          />
        </label>

        <button
          type="button"
          onClick={exportCsv}
          disabled={!summary}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>

        <button
          type="button"
          onClick={() => setRatesOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Rates & budget
        </button>
      </div>

      {error && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {loading && (
          <p className="py-12 text-center text-xs text-gray-500">Loading…</p>
        )}

        {!loading && summary && (
          <div className="space-y-4">
            {!summary.ratesConfigured && (
              <div className="flex items-start gap-2 rounded-lg border border-warning-500/30 bg-warning-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />
                <div className="text-xs text-black">
                  <p className="font-medium">No rates set yet.</p>
                  <p className="mt-0.5">
                    Conversations are being counted, but every cost shows as
                    zero until you enter what Meta charges you per conversation.
                  </p>
                  <button
                    type="button"
                    onClick={() => setRatesOpen(true)}
                    className="mt-1.5 rounded-lg bg-warning-600 px-2.5 py-1 text-[11px] font-medium text-white hover:opacity-90"
                  >
                    Set rates
                  </button>
                </div>
              </div>
            )}

            {budget?.enabled && (
              <div
                className={`rounded-lg border p-3 ${
                  budget.overBudget
                    ? 'border-danger-500/40 bg-danger-50'
                    : budget.alerting
                      ? 'border-warning-500/40 bg-warning-50'
                      : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-medium text-gray-900">
                    Monthly budget · {budget.month}
                  </span>
                  <span className="text-xs text-gray-600">
                    {money(budget.spent)} of {money(budget.budget)}
                  </span>
                </div>

                <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full transition-all ${
                      budget.overBudget
                        ? 'bg-danger-600'
                        : budget.alerting
                          ? 'bg-warning-600'
                          : 'bg-primary-600'
                    }`}
                    style={{ width: `${Math.min(budget.usedPercent, 100)}%` }}
                  />
                </div>

                <p className="mt-1.5 text-[11px] text-gray-600">
                  {budget.overBudget ? (
                    <span className="font-medium text-danger-600">
                      Over budget by {money(budget.spent - budget.budget)}.
                    </span>
                  ) : (
                    <>
                      {money(budget.remaining)} remaining ·{' '}
                      {budget.usedPercent.toFixed(1)}% used
                      {budget.alerting && (
                        <span className="font-medium text-warning-600">
                          {' '}
                          · past the {budget.threshold}% alert threshold
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: 'Total spent', value: money(summary.totalCost) },
                {
                  label: 'Billable conversations',
                  value: String(summary.totalConversations)
                },
                {
                  label: 'Free conversations',
                  value: String(summary.freeConversations),
                  hint: 'Service and free-entry-point'
                },
                {
                  label: 'Avg per conversation',
                  value:
                    summary.totalConversations > 0
                      ? money(summary.totalCost / summary.totalConversations)
                      : money(0)
                }
              ].map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-lg border border-gray-200 bg-white p-3"
                >
                  <p className="text-xs text-gray-500">{tile.label}</p>
                  <p className="mt-1 text-xl font-semibold text-gray-900">
                    {tile.value}
                  </p>
                  {tile.hint && (
                    <p className="text-[10px] text-gray-400">{tile.hint}</p>
                  )}
                </div>
              ))}
            </div>

            <Breakdown
              title="By category"
              rows={toRows(summary.byCategory)}
              money={money}
              labelOf={(name) => CATEGORY_LABEL[name] || name}
              empty="No conversations in this period."
            />

            <Breakdown
              title="By campaign"
              rows={toRows(summary.byBroadcast)}
              money={money}
              empty="No campaign sends in this period."
            />

            <Breakdown
              title="By template"
              rows={toRows(summary.byTemplate)}
              money={money}
              empty="No template sends in this period."
            />

            <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 p-3 text-[11px] text-gray-600">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                An estimate, not your Meta invoice. Meta reports which
                conversations are billable; the amounts come from the rates you
                entered. Per-country pricing, free-tier entry points and Meta's
                own rounding mean the real bill will differ. Tracking only
                covers conversations since billing was switched on.
              </span>
            </p>
          </div>
        )}
      </div>

      <BillingRatesDialog
        open={ratesOpen}
        onClose={() => setRatesOpen(false)}
        onSaved={load}
      />
    </div>
  );
};

/** One breakdown table. Shared by category, campaign and template. */
const Breakdown: React.FC<{
  title: string;
  rows: SpendRow[];
  money: (value: number) => string;
  labelOf?: (name: string) => string;
  empty: string;
}> = ({ title, rows, money, labelOf, empty }) => (
  <div className="rounded-lg border border-gray-200">
    <div className="flex items-center gap-1.5 border-b border-gray-200 px-3 py-2">
      <TrendingUp className="h-3.5 w-3.5 text-gray-400" />
      <span className="text-xs font-medium text-gray-900">{title}</span>
    </div>

    {rows.length === 0 ? (
      <p className="px-3 py-4 text-center text-xs text-gray-500">{empty}</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-medium text-gray-500">
            <th className="px-3 py-1.5">Name</th>
            <th className="px-3 py-1.5 text-right">Conversations</th>
            <th className="px-3 py-1.5 text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-t border-gray-100">
              <td className="px-3 py-1.5 text-xs text-gray-900">
                {labelOf ? labelOf(row.name) : row.name}
              </td>
              <td className="px-3 py-1.5 text-right text-xs text-gray-600">
                {row.conversations}
              </td>
              <td className="px-3 py-1.5 text-right text-xs font-medium text-gray-900">
                {money(row.cost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default WhatsAppBilling;
